// File: frontend/src/components/InstallPage.js
//
// The always-on display setup guide: how to put Steadily.me on a purpose-built
// device (on a wall or a desk) and make it what the device boots into. Every command here is
// also what the app's own "Start automatically at login" switch writes
// (steadily-godot/scripts/display/display_controllers.gd), so the two
// never disagree. Prerendered at build time (src/prerender.js): keep it
// free of browser APIs at render time.

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MarketingLayout from './marketing/MarketingLayout';
import { Article, ArticleHeader, Section, Callout } from './marketing/Article';
import { CodeBlock, Code } from './marketing/CodeBlock';

// The Android component that carries the HOME intent filter: the exported
// launcher alias of the Godot activity. Only the sideload APK declares it
// (the steadily/home_launcher export option); the Play build does not.
const HOME_COMPONENT = 'com.realitymoss.steadily/com.godot.game.GodotAppLauncher';

const SECTIONS = [
  { id: 'android', label: 'Android frames and tablets' },
  { id: 'linux', label: 'Linux and Raspberry Pi' },
  { id: 'windows', label: 'Windows' },
  { id: 'mac', label: 'macOS' },
];

function Steps({ children }) {
  return <ol className="list-decimal pl-6 space-y-6 marker:font-serif marker:font-bold">{children}</ol>;
}

function Step({ title, children }) {
  return (
    <li className="pl-1">
      <p className="font-semibold text-[var(--color-ink-main)]">{title}</p>
      <div className="mt-2 space-y-3">{children}</div>
    </li>
  );
}

function InstallPage() {
  return (
    <MarketingLayout>
      <Article>
        <ArticleHeader
          title="Set up an always-on display"
          lede="Turn an Android photo frame, an old tablet, a Raspberry Pi or a spare PC into an always-on Steadily.me display, on a wall or a desk, that boots straight into your habits and stays there."
        />

        <Callout title="Before you start" className="mt-10 mb-10">
          <p>
            Get the right build from the <Link to="/download" className="underline underline-offset-2">download page</Link>:
            the Android APK for photo frames and tablets, the Linux (ARM) zip for a Raspberry Pi, the Linux or
            Windows zip for a PC, the macOS zip for a Mac.
          </p>
          <p>
            The app updates itself overnight (Settings → Updates → "Install updates automatically overnight"),
            and Settings → Day &amp; night holds the sleep schedule, with "Sleep overnight" set to Screen off
            or Dim.
          </p>
          <p>
            The Android route needs a computer with <Code>adb</Code>, the Android command-line tool. The
            others need nothing beyond the device itself.
          </p>
        </Callout>

        <nav aria-label="On this page" className="mb-12 flex flex-wrap gap-2">
          {SECTIONS.map(s => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="px-4 py-2 rounded-full bg-[var(--color-surface-input)] border border-[var(--color-divider)] text-sm font-serif font-bold text-[var(--color-ink-main)] hover:bg-[var(--color-hover-fill)] transition-colors"
            >
              {s.label}
            </a>
          ))}
        </nav>

        <div className="space-y-16 text-[var(--color-ink-main)] leading-relaxed">
          <Section id="android" title="Android photo frames and old tablets">
            <p>
              Any Android 7.0 or newer device with OpenGL ES 3.0 graphics works, including the 10 to 15 inch
              digital photo frames that run Android, retired tablets and Fire tablets. You need the sideload APK from the download
              page, not the Play Store build: only the APK registers as a home app, which is what lets the
              device boot straight into Steadily.me. These steps were tested on a Frameo-based 15 inch frame
              running Android 8.1.
            </p>
            <p>
              Install <Code>adb</Code> on your computer first: on a Mac, <Code>brew install android-platform-tools</Code>;
              on Windows and Linux, download the Android SDK platform tools from developer.android.com and unzip
              them anywhere.
            </p>
            <Steps>
              <Step title="Turn on ADB on the device.">
                <p>
                  On a Frameo frame (the software most Android photo frames ship with): open the frame's Settings,
                  go to About, and turn on Beta Program. An ADB Access switch appears in the same About section
                  once you are in the beta; turn that on too. Some frames ship with ADB Access already showing, so
                  look for the switch before joining the beta.
                </p>
                <p>
                  On a stock Android tablet: Settings → About → tap Build number seven times, then Settings →
                  Developer options → USB debugging.
                </p>
              </Step>
              <Step title="Connect from your computer.">
                <p>
                  Use the frame's USB-C or micro USB port; a USB-A port on a frame is for thumb drives and cannot
                  talk to a computer. A USB-A to USB-C cable on the computer side is the safer choice: some frames
                  never negotiate over a USB-C to USB-C cable and simply don't show up. Accept the "Allow USB
                  debugging" prompt on the frame's screen, ticking "Always allow from this computer".
                </p>
                <CodeBlock code="adb devices" />
                <p>
                  If the frame isn't listed, turn ADB Access off and on again and replug the cable, which brings the
                  prompt back. On some Frameo frames the port only comes alive after turning on USB transfers
                  (Settings → Manage photos → Transfer from computer).
                </p>
              </Step>
              <Step title="Install the APK.">
                <CodeBlock code="adb install -r Steadily-me.apk" />
                <p>
                  <Code>-r</Code> keeps the app's data if it is already installed. If the streamed install
                  stalls, copying the file over first is more reliable:
                </p>
                <CodeBlock
                  label="If the install stalls"
                  code={'adb push Steadily-me.apk /data/local/tmp/\nadb shell pm install -r /data/local/tmp/Steadily-me.apk'}
                />
              </Step>
              <Step title="Open it once and sign in.">
                <p>
                  Launch Steadily.me from the app drawer, sign in, and set your schedule under Settings → Day &amp;
                  night and updates under Settings → Updates. The app stays on unless you tell it to sleep.
                </p>
              </Step>
              <Step title="Make it the home app.">
                <p>
                  This is what makes the device boot into Steadily.me instead of its own launcher. On Android 8 or
                  newer, set it from your computer:
                </p>
                <CodeBlock code={`adb shell cmd package set-home-activity ${HOME_COMPONENT}`} />
                <Callout tone="warning" as="h3" title="Wait about 45 seconds before rebooting">
                  <p>
                    Android writes the home-app choice to disk on a delay. Reboot right away and the choice is
                    lost: the device comes up asking "Select a Home app" instead. Give it a moment, then restart:
                  </p>
                  <CodeBlock code="adb reboot" />
                </Callout>
                <p>
                  Without adb, or on Android 7: press the device's Home button, pick Steadily.me from the
                  "Select a Home app" list and tap Always.
                </p>
              </Step>
              <Step title="Going back.">
                <p>
                  To return the device to its original launcher, list the home apps it knows and pick the other one:
                </p>
                <CodeBlock
                  code={'adb shell cmd package query-activities -a android.intent.action.MAIN -c android.intent.category.HOME\nadb shell cmd package set-home-activity <package>/<activity>'}
                />
                <p>
                  Or press Home and choose the other app with Always. Uninstalling Steadily.me also hands home back.
                </p>
              </Step>
            </Steps>
            <p>
              New releases reach the frame on their own: the app downloads small overnight updates, so you rarely
              need adb again after the first install.
            </p>
          </Section>

          <Section id="linux" title="Linux and Raspberry Pi">
            <p>
              A Raspberry Pi 4 or 5 running the 64-bit Raspberry Pi OS (Bullseye or newer), or any 64-bit distro
              with glibc 2.31 or newer and OpenGL 3.3 or OpenGL ES 3.0 graphics. Use the Linux (ARM) zip for a Pi
              and the Linux zip for an Intel or AMD machine; the file names below are the Pi's.
            </p>
            <Steps>
              <Step title="Unpack it.">
                <CodeBlock
                  code={'mkdir -p ~/steadily\nunzip Steadily-me-linux-arm64.zip -d ~/steadily\nchmod +x ~/steadily/Steadily-me.arm64'}
                />
                <p>
                  Keep <Code>Steadily-me.pck</Code> next to the binary; the app is the pair of them. On x86_64 the
                  binary is <Code>Steadily-me.x86_64</Code>.
                </p>
              </Step>
              <Step title="Run it once and sign in.">
                <CodeBlock code="~/steadily/Steadily-me.arm64" />
                <p>
                  It opens fullscreen. Steadily.me uses X11; on Raspberry Pi OS's Wayland desktop it runs under
                  XWayland automatically, nothing to configure. Touchscreens work as they are.
                </p>
              </Step>
              <Step title="Log the Pi in automatically.">
                <p>
                  On Raspberry Pi OS: <Code>sudo raspi-config</Code> → System Options → Boot / Auto Login →
                  Desktop Autologin. Other distros have the same switch in their display manager (GNOME: Settings →
                  Users → Automatic Login).
                </p>
              </Step>
              <Step title="Start Steadily.me at login.">
                <p>
                  The easy way is in the app: Settings → Appearance → "Start automatically at login". It writes a
                  standard autostart entry, which you can also create by hand (replace <Code>/home/pi</Code> with
                  your home directory):
                </p>
                <CodeBlock
                  label="~/.config/autostart/steadily.desktop"
                  code={'[Desktop Entry]\nType=Application\nName=Steadily.me\nComment=Habit wall painting\nExec="/home/pi/steadily/Steadily-me.arm64"\nX-GNOME-Autostart-enabled=true'}
                />
                <p>
                  If your desktop ignores autostart entries (Raspberry Pi OS with the labwc compositor), start it
                  from the compositor's own autostart file instead:
                </p>
                <CodeBlock
                  code={'mkdir -p ~/.config/labwc\necho \'/home/pi/steadily/Steadily-me.arm64 &\' >> ~/.config/labwc/autostart'}
                />
                <p>
                  A dedicated kiosk user, a systemd user service, or your distro's session autostart all work too:
                  whatever runs after the desktop is up, run the binary from it.
                </p>
              </Step>
              <Step title="Let it turn the screen off at night.">
                <p>
                  Steadily.me switches the panel off with whichever tool is installed: <Code>wlopm</Code> on Wayland
                  desktops (labwc, Wayfire), <Code>xset</Code> on X11, or <Code>brightnessctl</Code>. The "Screen off"
                  choice under Settings → Day &amp; night → Sleep overnight only appears when one of them exists;
                  if it is missing, install one and restart the app:
                </p>
                <CodeBlock
                  code={'sudo apt install wlopm            # Wayland desktops\nsudo apt install x11-xserver-utils # X11 desktops (provides xset)'}
                />
                <p>
                  Use "Test (off for 3 seconds)" on the same settings row to confirm the panel actually goes dark
                  and comes back.
                </p>
              </Step>
              <Step title="Signing in with Google or Apple on a stripped-down image.">
                <p>
                  Sign-in opens your browser. A kiosk image with no desktop browser can point the app at one by
                  exporting <Code>BROWSER=chromium</Code> (or whatever is installed) in the script that starts it.
                  Email and password sign-in needs no browser at all.
                </p>
              </Step>
            </Steps>
          </Section>

          <Section id="windows" title="Windows PCs and tablets">
            <p>
              Windows 10 or 11 on x64, with any graphics from roughly 2010 onward. An old Surface or a cheap
              mini PC behind a monitor both make good displays.
            </p>
            <Steps>
              <Step title="Unzip it.">
                <p>
                  Put <Code>Steadily-me.exe</Code> and <Code>Steadily-me.pck</Code> together in a folder that stays
                  put, for example <Code>C:\Steadily</Code>. The paths below assume that folder.
                </p>
              </Step>
              <Step title="Run it once.">
                <p>
                  The download is not code-signed, so the first launch shows SmartScreen's "Windows protected your
                  PC" screen: click More info, then Run anyway. Sign in and set your schedule under Settings →
                  Day &amp; night.
                </p>
              </Step>
              <Step title="Sign in to Windows automatically.">
                <p>
                  Press Win+R, run <Code>netplwiz</Code>, select your account and untick "Users must enter a user
                  name and password to use this computer". On Windows 11 with Windows Hello, first turn off
                  "For improved security, only allow Windows Hello sign-in" under Settings → Accounts → Sign-in
                  options, or netplwiz hides the checkbox.
                </p>
              </Step>
              <Step title="Start Steadily.me at login.">
                <p>
                  In the app: Settings → Appearance → "Start automatically at login". It registers a Run entry for
                  your account, the same one this command creates:
                </p>
                <CodeBlock
                  code={String.raw`reg add "HKCU\Software\Microsoft\Windows\CurrentVersion\Run" /v Steadily.me /t REG_SZ /d "C:\Steadily\Steadily-me.exe" /f`}
                />
                <p>
                  Also stop Windows from sleeping the PC on its own: Settings → System → Power, set "Put my device
                  to sleep" to Never, and let Steadily.me manage the screen instead.
                </p>
              </Step>
              <Step title="Make it the shell (the launcher equivalent).">
                <p>
                  Windows can start Steadily.me instead of the desktop for one account. The taskbar and Start menu
                  don't load for that account; Steadily.me is all there is, which is exactly right for a display:
                </p>
                <CodeBlock
                  code={String.raw`reg add "HKCU\Software\Microsoft\Windows NT\CurrentVersion\Winlogon" /v Shell /t REG_SZ /d "C:\Steadily\Steadily-me.exe" /f`}
                />
                <p>
                  To get the desktop back for a moment, press Ctrl+Alt+Del, open Task Manager, and run
                  <Code>explorer.exe</Code> as a new task. To undo it for good:
                </p>
                <CodeBlock
                  code={String.raw`reg delete "HKCU\Software\Microsoft\Windows NT\CurrentVersion\Winlogon" /v Shell /f`}
                />
                <p>
                  On Windows Enterprise, Education and IoT editions, Shell Launcher (Settings → Accounts → Other
                  users → Set up a kiosk, or the Shell Launcher policy) is the supported way to do the same, and
                  survives Windows updates more gracefully.
                </p>
              </Step>
            </Steps>
          </Section>

          <Section id="mac" title="macOS">
            <p>
              Unzip <Code>Steadily.me.app</Code> and move it to Applications. The app is signed but not notarized,
              so the first time, right-click it and choose Open, then Open again in the dialog; after that it opens
              normally. Settings → Appearance → "Start automatically at login" registers a launch agent
              (<Code>~/Library/LaunchAgents/me.steadily.app.plist</Code>) so it comes back after a restart. Pair
              it with automatic login under System Settings → Users &amp; Groups, and let Steadily.me sleep the
              display on its own schedule.
            </p>
          </Section>
        </div>

        <p className="mt-16 text-[var(--color-ink-light)]">
          Stuck on a device this page doesn't cover? Write to{' '}
          <a href="mailto:support@steadily.me" className="underline">support@steadily.me</a> and we'll work it out
          together. Or{' '}
          <Link to="/download" className="inline-flex items-center gap-1 underline underline-offset-2">
            head back to the downloads <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </p>
      </Article>
    </MarketingLayout>
  );
}

export default InstallPage;
